/**
 * Prioriza as tarefas de UM cliente, a partir dos grupos do CRM.
 *
 * ⚠️ A MESMA regra existe hoje dentro do `profile-tarefas.vue`, na página
 * `/cliente-perfil`. Ela NÃO foi extraída de lá: aquele componente é de outra
 * frente e não se mexe por causa desta. A consequência é conhecida e aceita —
 * um ajuste na prioridade precisa ser feito nos dois lugares. Unificar é
 * conversa para quando as duas frentes se encontrarem.
 *
 * O que sai daqui é DADO. Rótulo, cor e destino do clique ficam com quem
 * desenha.
 */

// Grupos que são TAREFA, em ordem de prioridade. Número menor = mais urgente.
// Grupo fora deste mapa não é tarefa e fica de fora.
//
// ⚠️ `tarefas` (manual pendente) precisa constar: o backend classifica aí a
// tarefa manual recém-criada, com prazo no futuro. Sem ele, ela sumia do card.
//
// ⚠️ `relacionamento_atrasadas` também: são atividades de régua vencidas, e
// deixá-las de fora fazia a contagem de atrasadas ficar abaixo da real.
const PRIORIDADE_DO_GRUPO = {
  tarefas_criticas: 0,
  relacionamento_atrasadas: 1,
  tarefas_manuais_atrasadas: 2,
  tarefas: 3,
}

const GRUPO_CRITICAS = 'tarefas_criticas'

export const STATUS_CRITICA = 'critica'
export const STATUS_ATRASADA = 'atrasada'
export const STATUS_PENDENTE = 'pendente'

const ZERO = 0

/**
 * Tag da tarefa: crítica vem do GRUPO e tem precedência; o resto se decide pelo
 * atraso.
 */
const statusDaTarefa = (identificador, atrasada) => {
  if (identificador === GRUPO_CRITICAS) return STATUS_CRITICA
  return atrasada ? STATUS_ATRASADA : STATUS_PENDENTE
}

/**
 * ⚠️ `> 0`, e não "existe": no grupo de régua o backend manda `0` para a tarefa
 * que vence HOJE — e vencer hoje não é estar atrasado.
 */
const estaAtrasada = (tarefa) => Number(tarefa.dias_em_atraso) > ZERO

/**
 * Achata os grupos relevantes numa lista, anexando a prioridade do grupo e a
 * ordem de chegada (desempate estável na ordenação).
 */
const achatar = (grupos) => {
  const itens = []

  ;(grupos || []).forEach((grupo) => {
    const prioridade = PRIORIDADE_DO_GRUPO[grupo && grupo.identificador]
    if (prioridade === undefined) return

    ;(grupo.tarefas || []).forEach((tarefa) => {
      const atrasada = estaAtrasada(tarefa)
      itens.push({
        prioridade,
        ordem: itens.length,
        atrasada,
        tarefa: {
          titulo: tarefa.titulo,
          prazo: tarefa.data_prevista,
          status: statusDaTarefa(grupo.identificador, atrasada),
          // Grupo de origem: é por ele que o CRM distingue a tarefa manual das
          // demais, e é o que decide para onde o clique leva.
          grupo: grupo.identificador,
          // A tarefa como veio do backend: o modal de relacionamento lê dela o
          // cliente (bloco aninhado), a régua (metadata) e o tipo.
          raw: tarefa,
        },
      })
    })
  })

  return itens
}

/**
 * @param {Array<Object>} grupos `response.value.grupos` do CRM
 * @param {Object=} opcoes `{ maximo }` — quantas devolver em `tarefas`
 * @returns {{tarefas: Array, totalAtrasadas: number, temMais: boolean}}
 */
export const priorizarTarefas = (grupos, opcoes) => {
  const maximo = (opcoes || {}).maximo
  const itens = achatar(grupos)

  // Prioridade do grupo primeiro; no empate, a ordem que o backend já mandou —
  // ele entrega cada grupo ordenado por urgência.
  itens.sort((a, b) => a.prioridade - b.prioridade || a.ordem - b.ordem)

  // ⚠️ Conta o atraso na lista COMPLETA, não só no que vai aparecer: o número
  // é "quantas estão atrasadas", não "quantas atrasadas couberam".
  const totalAtrasadas = itens.filter((item) => item.atrasada).length
  const teto = maximo === undefined ? itens.length : maximo

  return {
    tarefas: itens.slice(0, teto).map((item) => item.tarefa),
    totalAtrasadas,
    temMais: itens.length > teto,
  }
}
